"use client";

import { createContext, useCallback, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light";

interface ThemeContextValue {
  theme: Theme;
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: "dark",
  setTheme: () => {},
});

export function useTheme() {
  return useContext(ThemeContext);
}

/**
 * Couleur des zones sures d'iOS (barre d'etat, bas de l'ecran).
 *
 * Elles ne suivent NI le <body> NI le thEme CSS : seul `theme-color` les
 * gouverne. Sans mise a jour, basculer en clair laissait une bande bleu nuit
 * en haut de l'ecran, heritee du manifeste.
 */
function appliquerCouleurBarre(theme: Theme) {
  if (typeof document === "undefined") return;
  let m = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
  if (!m) {
    m = document.createElement("meta");
    m.name = "theme-color";
    document.head.appendChild(m);
  }
  m.content = theme === "dark" ? "#0A0E19" : "#F7F9FC";
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>("dark");

  // Read from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("theme") as Theme | null;
    if (stored === "light" || stored === "dark") {
      setThemeState(stored);
    }
  }, []);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    localStorage.setItem("theme", t);
    const root = document.documentElement;
    if (t === "dark") {
      root.classList.add("dark");
      appliquerCouleurBarre("dark");
    } else {
      root.classList.remove("dark");
      appliquerCouleurBarre("light");
    }
    // Persist to DB (fire-and-forget)
    fetch("/api/user/theme", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ theme: t }),
    }).catch(() => {});
  }, []);

  // Sync class on mount (in case SSR/inline script state differs)
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      root.classList.add("dark");
      appliquerCouleurBarre("dark");
    } else {
      root.classList.remove("dark");
      appliquerCouleurBarre("light");
    }
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
